import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Row, Col, Spinner, Modal, Alert } from 'react-bootstrap';
import {
  FiX, FiArrowRight, FiArrowLeft, FiCheck, FiUpload, FiTrash, FiInfo,
  FiDroplet, FiTag, FiBookOpen, FiMessageSquare, FiImage, FiMic,
  FiTarget, FiPenTool, FiHeart, FiCrop, FiCheckCircle, FiGrid, FiGlobe,
  FiBriefcase, FiUser, FiUsers, FiPackage, FiHash, FiMonitor
} from 'react-icons/fi';
import Cropper from 'react-easy-crop';
import { useLanguage } from '../../Context/LanguageContext';
import { PrimaryButton, CancelButton, OutlinedDangerButton } from '../../Components/Buttons';
import { ColorPickerField, MultiSelectField, SingleChoiceField } from './FormFields';
import { packFieldMapping } from '../utils/packConfig';
import PackModal from './PackModal';
import CustomTooltip from '../../Components/CustomTooltip';
import AIBadge from '../../Components/AIBadge';
import { getCroppedImg } from '../utils/cropUtils';
import '../Onboarding.css';
import './SetupModal.css';

const TOTAL_STEPS = 4;

const STEP_CONFIG = [
  { id: 'brand-basics', icon: <FiTag size={18} /> },
  { id: 'identity-core', icon: <FiGrid size={18} /> },
  { id: 'presence-offerings', icon: <FiGlobe size={18} /> },
  { id: 'enhancement-hub', icon: <FiHeart size={18} /> },
];

const SetupModal = ({
  show,
  onClose,
  formData,
  onChange,
  brandType,
  logoPreview,
  onFileChange,
  onRemoveLogo,
  fileInputRef,
  onCreateBrand,
  onUpdateBrand,
  brandCreated,
  aiSuggestions = {}
}) => {
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [shaking, setShaking] = useState(false);
  const [brandNameError, setBrandNameError] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const debounceRef = useRef(null);
  const bodyRef = useRef(null);

  // Cropping state
  const [showCropModal, setShowCropModal] = useState(false);
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropError, setCropError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  // Enhancement Hub state
  const [activePackModal, setActivePackModal] = useState(null);
  const [isSavingPack, setIsSavingPack] = useState(false);

  // Helper to check if a field has AI suggestion
  const hasAiSuggestion = (fieldName) => aiSuggestions.hasOwnProperty(fieldName);
  const getAiConfidence = (fieldName) => aiSuggestions[fieldName] || 0;

  // Pack completion check
  const isPackCompleted = (packId) => {
    const fields = packFieldMapping[packId] || [];
    return fields.some(field => {
      const val = formData[field];
      return Array.isArray(val) ? val.length > 0 : val && val !== '';
    });
  };

  // Step titles
  const getStepTitle = (step) => {
    switch (step) {
      case 1: return t('onboarding.setupModal.stepBrandBasics') || 'Brand Basics';
      case 2: return t('onboarding.setupModal.stepIdentityCore') || 'Identity & Core';
      case 3: return t('onboarding.setupModal.stepPresenceOfferings') || 'Presence & Offerings';
      case 4: return t('onboarding.setupModal.stepEnhancementHub') || 'Enhancement Hub';
      default: return '';
    }
  };

  // Scroll body to top on step change
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Auto-save with debounce (only when brand is already created)
  useEffect(() => {
    if (!brandCreated || !onUpdateBrand) return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      onUpdateBrand();
    }, 800);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, brandCreated]);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // --- Close handler ---
  const handleClose = async () => {
    if (currentStep === 1 && !formData.company_name?.trim()) {
      setShaking(true);
      setBrandNameError(true);
      setTimeout(() => setShaking(false), 400);
      return;
    }

    // Save before closing
    try {
      if (!brandCreated && onCreateBrand && formData.company_name?.trim()) {
        await onCreateBrand();
      } else if (brandCreated && onUpdateBrand) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        await onUpdateBrand();
      }
    } catch (err) {
      // proceed with close even if save fails
    }

    onClose();
  };

  // --- Navigation ---
  const handleContinue = async () => {
    if (currentStep === 1) {
      if (!formData.company_name?.trim()) return;

      if (!brandCreated && onCreateBrand) {
        setIsCreating(true);
        try {
          await onCreateBrand();
        } catch (err) {
          // error is handled by parent
        } finally {
          setIsCreating(false);
        }
      }
    }

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    try {
      if (brandCreated && onUpdateBrand) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        await onUpdateBrand();
      }
    } catch (err) {
      // proceed with close even if save fails
    }
    onClose();
  };

  // --- Cropping Handlers ---
  const handleFileChangeInternal = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      if (file.size > 3 * 1024 * 1024) {
        setCropError(t('onboarding.errorLogoSize') || 'File too large (max 3MB)');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'].includes(file.type)) {
        setCropError(t('onboarding.errorLogoType') || 'Invalid file type');
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }
      const fileUrl = URL.createObjectURL(file);
      setImgSrc(fileUrl);
      setShowCropModal(true);
      setCropError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChangeInternal({ target: { files: e.dataTransfer.files } });
    }
  };

  const onCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const resetCropState = () => {
    if (imgSrc) {
      URL.revokeObjectURL(imgSrc);
    }
    setImgSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setCropError('');
  };

  const handleCropCancel = () => {
    resetCropState();
    setShowCropModal(false);
  };

  const handleCropImage = async () => {
    if (!croppedAreaPixels || !imgSrc) {
      setCropError(t('branding.brandsettings.errorCrop') || 'Please adjust the crop before saving.');
      return;
    }
    try {
      const { blob } = await getCroppedImg(imgSrc, croppedAreaPixels, `brand-logo-${Date.now()}.png`);
      const croppedFile = new File([blob], blob.name || 'cropped-logo.png', { type: blob.type });
      const syntheticEvent = { target: { files: [croppedFile] } };
      onFileChange(syntheticEvent);
      setShowCropModal(false);
      resetCropState();
    } catch (error) {
      setCropError(t('branding.brandsettings.errorCropFailed') || 'Something went wrong while cropping.');
    }
  };

  // --- Field handlers for steps 2-3 ---
  const handleSingleChoice = (e) => {
    onChange(e);
  };

  // --- Render Step 1: Brand Basics ---
  const renderBrandBasics = () => {
    const tooltips = {
      logo: t('onboarding.tooltips.logo') || 'Upload the primary logo/avatar BV should use in mockups, visuals or brand examples.',
      colors: t('onboarding.tooltips.colors') || 'Define your main brand colors so templates and visuals stay on-brand.',
      story: t('onboarding.tooltips.story') || 'Short version of how you started, what you do today and why you exist.',
      tagline: t('onboarding.tooltips.tagline') || 'Your main slogan or tagline that expresses your promise in one line.'
    };

    return (
      <>
        {/* Brand Name */}
        <Form.Group className="mb-4">
          <Form.Label className="onboarding-form-label">
            <FiTag size={16} style={{ color: 'var(--primary)' }} />
            <span>{t('onboarding.setupModal.brandName') || 'Brand Name'}</span>
            <span className="text-danger">*</span>
            {hasAiSuggestion('company_name') && <AIBadge confidence={getAiConfidence('company_name')} className="ai-badge-inline" />}
          </Form.Label>
          <Form.Control
            type="text"
            name="company_name"
            value={formData.company_name || ''}
            onChange={(e) => {
              if (brandNameError) setBrandNameError(false);
              onChange(e);
            }}
            placeholder={t('onboarding.setupModal.brandNamePlaceholder') || 'Enter your brand name'}
            required
            className="onboarding-form-input"
            autoFocus
            style={brandNameError ? { borderColor: 'var(--onboarding-danger, #ef4444)', boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.15)' } : {}}
          />
          {brandNameError && (
            <Form.Text className="text-danger mt-1 d-block" style={{ fontSize: '0.8rem' }}>
              {t('onboarding.errorBrandNameRequired') || 'Brand name is required.'}
            </Form.Text>
          )}
        </Form.Group>

        {/* Logo Upload */}
        <Form.Group className="mb-4">
          <Form.Label className="onboarding-form-label">
            <FiImage size={16} style={{ color: 'var(--primary)' }} />
            <span>{t('onboarding.brandLogoLabel') || 'Brand Logo'}</span>
            <CustomTooltip content={tooltips.logo} placement="top" showOnMobile={true}>
              <span className="onboarding-info-icon-wrapper"><FiInfo size={14} /></span>
            </CustomTooltip>
          </Form.Label>

          {!logoPreview ? (
            <div
              className={`onboarding-logo-upload-area ${dragActive ? 'drag-active' : ''}`}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Form.Control
                ref={fileInputRef}
                type="file"
                onChange={handleFileChangeInternal}
                accept="image/png, image/jpeg, image/svg+xml, image/webp"
                className="d-none"
              />
              <FiUpload size={28} className="mb-2" style={{ color: 'var(--primary)' }} />
              <div className="text-muted small">
                <span className="fw-bold" style={{ color: 'var(--primary)' }}>{t('onboarding.logoUploadTitle') || 'Click to upload'}</span> {t('onboarding.logoUploadOr') || 'or drag and drop'}
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{t('onboarding.logoUploadFormats') || 'SVG, PNG, JPG (max 3MB)'}</div>
            </div>
          ) : (
            <div className="onboarding-logo-preview-container d-flex align-items-center justify-content-between">
              <img src={logoPreview} alt="Logo Preview" className="onboarding-logo-preview" />
              <OutlinedDangerButton size="sm" onClick={onRemoveLogo}>
                <FiTrash size={16} />
              </OutlinedDangerButton>
            </div>
          )}
        </Form.Group>

        {/* Brand Colors */}
        <Form.Group className="mb-4">
          <Form.Label className="onboarding-form-label">
            <FiDroplet size={16} style={{ color: 'var(--primary)' }} />
            <span>{t('onboarding.brandColorsTitle') || 'Brand Colors'}</span>
            <CustomTooltip content={tooltips.colors} placement="top" showOnMobile={true}>
              <span className="onboarding-info-icon-wrapper"><FiInfo size={14} /></span>
            </CustomTooltip>
          </Form.Label>
          <Row className="g-3">
            <Col xs={12} sm={4}>
              <ColorPickerField
                label={t('onboarding.colorPrimary') || 'Primary'}
                name="main_color"
                value={formData.main_color}
                onChange={onChange}
              />
            </Col>
            <Col xs={6} sm={4}>
              <ColorPickerField
                label={t('onboarding.colorSecondary') || 'Secondary'}
                name="secondary_color"
                value={formData.secondary_color}
                onChange={onChange}
                placeholder="#Optional"
              />
            </Col>
            <Col xs={6} sm={4}>
              <ColorPickerField
                label={t('onboarding.colorAccent') || 'Accent'}
                name="accent_color"
                value={formData.accent_color}
                onChange={onChange}
                placeholder="#Optional"
              />
            </Col>
          </Row>
        </Form.Group>

        {/* Tagline */}
        <Form.Group className="mb-4">
          <Form.Label className="onboarding-form-label">
            <FiMessageSquare size={16} style={{ color: 'var(--primary)' }} />
            <span>{t('onboarding.tagline') || 'Tagline / Slogan'}</span>
            {hasAiSuggestion('tagline') && <AIBadge confidence={getAiConfidence('tagline')} className="ai-badge-inline" />}
            <CustomTooltip content={tooltips.tagline} placement="top" showOnMobile={true}>
              <span className="onboarding-info-icon-wrapper"><FiInfo size={14} /></span>
            </CustomTooltip>
          </Form.Label>
          <Form.Control
            type="text"
            name="tagline"
            value={formData.tagline || ''}
            onChange={onChange}
            placeholder={t('onboarding.taglinePlaceholderNew') || 'Your main slogan or tagline'}
            className="onboarding-form-input"
          />
        </Form.Group>

        {/* Brand Story */}
        <Form.Group className="mb-4">
          <Form.Label className="onboarding-form-label">
            <FiBookOpen size={16} style={{ color: 'var(--primary)' }} />
            <span>{t('onboarding.brandStory') || 'Brand Story'}</span>
            {hasAiSuggestion('brand_story') && <AIBadge confidence={getAiConfidence('brand_story')} className="ai-badge-inline" />}
            <CustomTooltip content={tooltips.story} placement="top" showOnMobile={true}>
              <span className="onboarding-info-icon-wrapper"><FiInfo size={14} /></span>
            </CustomTooltip>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            name="brand_story"
            value={formData.brand_story || ''}
            onChange={onChange}
            placeholder={t('onboarding.brandStoryPlaceholder') || 'Brief narrative about your brand (origin, mission, what makes you unique)'}
            className="onboarding-form-input"
          />
        </Form.Group>
      </>
    );
  };

  // --- Render Step 2: Identity & Core ---
  const renderIdentityCore = () => {
    if (brandType === 'company') {
      // Organization Type options
      const orgTypeOptions = [
        { value: 'Startup (early or growth stage)', label: t('onboarding.orgTypes.startup'), description: t('onboarding.orgTypes.startupDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Small / Medium Business (SMB)', label: t('onboarding.orgTypes.smb'), description: t('onboarding.orgTypes.smbDesc'), icon: <FiBriefcase size={16} /> },
        { value: 'Enterprise / Large organization', label: t('onboarding.orgTypes.enterprise'), description: t('onboarding.orgTypes.enterpriseDesc'), icon: <FiGrid size={16} /> },
        { value: 'Agency / Consultancy', label: t('onboarding.orgTypes.agency'), description: t('onboarding.orgTypes.agencyDesc'), icon: <FiUsers size={16} /> },
        { value: 'Non-profit / NGO', label: t('onboarding.orgTypes.nonprofit'), description: t('onboarding.orgTypes.nonprofitDesc'), icon: <FiHeart size={16} /> },
        { value: 'Public institution / Government-related', label: t('onboarding.orgTypes.public'), description: t('onboarding.orgTypes.publicDesc'), icon: <FiGlobe size={16} /> },
      ];

      // Industry options
      const industryOptions = [
        { value: 'Technology / SaaS', label: t('onboarding.industries.techSaas'), description: t('onboarding.industries.techSaasDesc'), icon: <FiMonitor size={16} /> },
        { value: 'E-commerce & Online Retail', label: t('onboarding.industries.ecommerce'), description: t('onboarding.industries.ecommerceDesc'), icon: <FiPackage size={16} /> },
        { value: 'Professional Services', label: t('onboarding.industries.profServices'), description: t('onboarding.industries.profServicesDesc'), icon: <FiBriefcase size={16} /> },
        { value: 'Manufacturing & Engineering', label: t('onboarding.industries.manufacturing'), description: t('onboarding.industries.manufacturingDesc'), icon: <FiGrid size={16} /> },
        { value: 'Finance, Banking & Fintech', label: t('onboarding.industries.finance'), description: t('onboarding.industries.financeDesc'), icon: <FiHash size={16} /> },
        { value: 'Healthcare & Wellness', label: t('onboarding.industries.healthcare'), description: t('onboarding.industries.healthcareDesc'), icon: <FiHeart size={16} /> },
        { value: 'Education & Training', label: t('onboarding.industries.education'), description: t('onboarding.industries.educationDesc'), icon: <FiBookOpen size={16} /> },
        { value: 'Media, Marketing & Creative', label: t('onboarding.industries.media'), description: t('onboarding.industries.mediaDesc'), icon: <FiImage size={16} /> },
        { value: 'Hospitality, Travel & Tourism', label: t('onboarding.industries.travel'), description: t('onboarding.industries.travelDesc'), icon: <FiGlobe size={16} /> },
        { value: 'Public Sector / Government', label: t('onboarding.industries.government'), description: t('onboarding.industries.governmentDesc'), icon: <FiGlobe size={16} /> },
      ];

      return (
        <Form>
          <SingleChoiceField
            label={t('onboarding.orgTypeLabel') || 'Organization Type'}
            name="organization_type"
            value={formData.organization_type || ''}
            options={orgTypeOptions}
            onChange={handleSingleChoice}
            description={t('onboarding.orgTypeDesc') || 'What kind of organization are you?'}
            icon={<FiBriefcase size={16} />}
            compact={true}
          />
          <MultiSelectField
            label={t('onboarding.industryLabel') || 'Industry'}
            name="industry"
            value={formData.industry || []}
            options={industryOptions}
            onChange={onChange}
            max={3}
            description={t('onboarding.industryDesc') || 'Select up to 3 industries.'}
            icon={<FiGrid size={16} />}
            compact={true}
          />
        </Form>
      );
    } else {
      // Person: creator type + niche
      const creatorTypeOptions = [
        { value: 'Individual creator', label: t('onboarding.creatorTypes.individual'), description: t('onboarding.creatorTypes.individualDesc'), icon: <FiUser size={16} /> },
        { value: 'Duo / Team channel', label: t('onboarding.creatorTypes.team'), description: t('onboarding.creatorTypes.teamDesc'), icon: <FiUsers size={16} /> },
        { value: 'Company / Organization', label: t('onboarding.creatorTypes.organization'), description: t('onboarding.creatorTypes.organizationDesc'), icon: <FiBriefcase size={16} /> },
      ];

      const nicheOptions = [
        { value: 'Education & How-to', label: t('onboarding.niches.education'), description: t('onboarding.niches.educationDesc'), icon: <FiBookOpen size={16} /> },
        { value: 'Entertainment & Comedy', label: t('onboarding.niches.entertainment'), description: t('onboarding.niches.entertainmentDesc'), icon: <FiHeart size={16} /> },
        { value: 'Gaming', label: t('onboarding.niches.gaming'), description: t('onboarding.niches.gamingDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Business & Finance', label: t('onboarding.niches.business'), description: t('onboarding.niches.businessDesc'), icon: <FiBriefcase size={16} /> },
        { value: 'Tech & AI', label: t('onboarding.niches.techAi'), description: t('onboarding.niches.techAiDesc'), icon: <FiGrid size={16} /> },
        { value: 'Health, Fitness & Wellness', label: t('onboarding.niches.health'), description: t('onboarding.niches.healthDesc'), icon: <FiHeart size={16} /> },
        { value: 'Sleep, Relaxation & ASMR', label: t('onboarding.niches.relaxation'), description: t('onboarding.niches.relaxationDesc'), icon: <FiHeart size={16} /> },
        { value: 'Parenting & Family', label: t('onboarding.niches.family'), description: t('onboarding.niches.familyDesc'), icon: <FiUsers size={16} /> },
        { value: 'Fashion & Beauty', label: t('onboarding.niches.fashion'), description: t('onboarding.niches.fashionDesc'), icon: <FiImage size={16} /> },
        { value: 'Travel & Lifestyle', label: t('onboarding.niches.travelLifestyle'), description: t('onboarding.niches.travelLifestyleDesc'), icon: <FiGlobe size={16} /> },
        { value: 'News & Commentary', label: t('onboarding.niches.news'), description: t('onboarding.niches.newsDesc'), icon: <FiMessageSquare size={16} /> },
        { value: 'Music & Performance', label: t('onboarding.niches.music'), description: t('onboarding.niches.musicDesc'), icon: <FiMic size={16} /> },
      ];

      return (
        <Form>
          <SingleChoiceField
            label={t('onboarding.creatorTypeLabel') || 'Creator Type'}
            name="creator_type"
            value={formData.creator_type || ''}
            options={creatorTypeOptions}
            onChange={handleSingleChoice}
            description={t('onboarding.creatorTypeDesc') || 'What kind of creator are you?'}
            icon={<FiUser size={16} />}
            compact={true}
          />
          <SingleChoiceField
            label={t('onboarding.nicheLabel') || 'Primary Niche'}
            name="primary_niche"
            value={formData.primary_niche || ''}
            options={nicheOptions}
            onChange={handleSingleChoice}
            description={t('onboarding.nicheDesc') || 'What is your main content category?'}
            icon={<FiHash size={16} />}
            compact={true}
          />
        </Form>
      );
    }
  };

  // --- Render Step 3: Presence & Offerings ---
  const renderPresenceOfferings = () => {
    if (brandType === 'company') {
      const productsServicesOptions = [
        { value: 'Digital products (software, apps, SaaS)', label: t('onboarding.productsServices.digitalSaas'), description: t('onboarding.productsServices.digitalSaasDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Physical products / hardware', label: t('onboarding.productsServices.physical'), description: t('onboarding.productsServices.physicalDesc'), icon: <FiPackage size={16} /> },
        { value: 'Consulting & advisory', label: t('onboarding.productsServices.consulting'), description: t('onboarding.productsServices.consultingDesc'), icon: <FiBriefcase size={16} /> },
        { value: 'Implementation / integration services', label: t('onboarding.productsServices.implementation'), description: t('onboarding.productsServices.implementationDesc'), icon: <FiGrid size={16} /> },
        { value: 'Managed services / outsourcing', label: t('onboarding.productsServices.managed'), description: t('onboarding.productsServices.managedDesc'), icon: <FiUsers size={16} /> },
        { value: 'Training & education', label: t('onboarding.productsServices.training'), description: t('onboarding.productsServices.trainingDesc'), icon: <FiBookOpen size={16} /> },
        { value: 'Support & maintenance', label: t('onboarding.productsServices.support'), description: t('onboarding.productsServices.supportDesc'), icon: <FiHeart size={16} /> },
        { value: 'Licensing / subscriptions', label: t('onboarding.productsServices.licensing'), description: t('onboarding.productsServices.licensingDesc'), icon: <FiHash size={16} /> },
        { value: 'Events, workshops or experiences', label: t('onboarding.productsServices.events'), description: t('onboarding.productsServices.eventsDesc'), icon: <FiGlobe size={16} /> },
      ];

      const platformOptions = [
        { value: 'Corporate website', label: t('onboarding.companyPlatforms.website'), description: t('onboarding.companyPlatforms.websiteDesc'), icon: <FiGlobe size={16} /> },
        { value: 'LinkedIn Company Page', label: t('onboarding.companyPlatforms.linkedin'), description: t('onboarding.companyPlatforms.linkedinDesc'), icon: <FiUsers size={16} /> },
        { value: 'Instagram', label: t('onboarding.companyPlatforms.instagram'), description: t('onboarding.companyPlatforms.instagramDesc'), icon: <FiImage size={16} /> },
        { value: 'YouTube', label: t('onboarding.companyPlatforms.youtube'), description: t('onboarding.companyPlatforms.youtubeDesc'), icon: <FiMonitor size={16} /> },
        { value: 'X (Twitter)', label: t('onboarding.companyPlatforms.twitter'), description: t('onboarding.companyPlatforms.twitterDesc'), icon: <FiMessageSquare size={16} /> },
        { value: 'Facebook', label: t('onboarding.companyPlatforms.facebook'), description: t('onboarding.companyPlatforms.facebookDesc'), icon: <FiUsers size={16} /> },
        { value: 'TikTok', label: t('onboarding.companyPlatforms.tiktok'), description: t('onboarding.companyPlatforms.tiktokDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Email newsletter', label: t('onboarding.companyPlatforms.newsletter'), description: t('onboarding.companyPlatforms.newsletterDesc'), icon: <FiMessageSquare size={16} /> },
        { value: 'Partner marketplaces / app stores', label: t('onboarding.companyPlatforms.marketplaces'), description: t('onboarding.companyPlatforms.marketplacesDesc'), icon: <FiPackage size={16} /> },
        { value: 'Offline events & conferences', label: t('onboarding.companyPlatforms.offlineEvents'), description: t('onboarding.companyPlatforms.offlineEventsDesc'), icon: <FiGlobe size={16} /> },
      ];

      return (
        <Form>
          <MultiSelectField
            label={t('onboarding.productsServicesLabel') || 'Products & Services'}
            name="products_services"
            value={formData.products_services || []}
            options={productsServicesOptions}
            onChange={onChange}
            max={5}
            description={t('onboarding.productsServicesDesc') || 'What do you offer? Select up to 5.'}
            icon={<FiPackage size={16} />}
            compact={true}
          />
          <MultiSelectField
            label={t('onboarding.platformsLabel') || 'Platforms'}
            name="platforms"
            value={formData.platforms || []}
            options={platformOptions}
            onChange={onChange}
            max={10}
            description={t('onboarding.platformsDesc') || 'Where is your brand active?'}
            icon={<FiGlobe size={16} />}
            compact={true}
          />
        </Form>
      );
    } else {
      // Person
      const creatorServicesOptions = [
        { value: 'Learn new skills', label: t('onboarding.creatorServices.teachSkills'), description: t('onboarding.creatorServices.teachSkillsDesc'), icon: <FiBookOpen size={16} /> },
        { value: 'Relax, sleep, or reduce stress', label: t('onboarding.creatorServices.relaxation'), description: t('onboarding.creatorServices.relaxationDesc'), icon: <FiHeart size={16} /> },
        { value: 'Get motivated / inspired', label: t('onboarding.creatorServices.motivation'), description: t('onboarding.creatorServices.motivationDesc'), icon: <FiTarget size={16} /> },
        { value: 'Stay informed', label: t('onboarding.creatorServices.newsInfo'), description: t('onboarding.creatorServices.newsInfoDesc'), icon: <FiMessageSquare size={16} /> },
        { value: 'Be entertained / have fun', label: t('onboarding.creatorServices.entertainmentFun'), description: t('onboarding.creatorServices.entertainmentFunDesc'), icon: <FiHeart size={16} /> },
        { value: 'Improve career or business', label: t('onboarding.creatorServices.career'), description: t('onboarding.creatorServices.careerDesc'), icon: <FiBriefcase size={16} /> },
        { value: 'Improve health / fitness', label: t('onboarding.creatorServices.healthFitness'), description: t('onboarding.creatorServices.healthFitnessDesc'), icon: <FiHeart size={16} /> },
        { value: 'Improve relationships / parenting', label: t('onboarding.creatorServices.relationships'), description: t('onboarding.creatorServices.relationshipsDesc'), icon: <FiUsers size={16} /> },
        { value: 'Discover products / recommendations', label: t('onboarding.creatorServices.discovery'), description: t('onboarding.creatorServices.discoveryDesc'), icon: <FiGlobe size={16} /> },
      ];

      const creatorPlatformOptions = [
        { value: 'YouTube (long videos)', label: t('onboarding.creatorPlatforms.youtubeLong'), description: t('onboarding.creatorPlatforms.youtubeLongDesc'), icon: <FiMonitor size={16} /> },
        { value: 'YouTube Shorts', label: t('onboarding.creatorPlatforms.youtubeShorts'), description: t('onboarding.creatorPlatforms.youtubeShortsDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Instagram (posts & Reels)', label: t('onboarding.creatorPlatforms.instagramReels'), description: t('onboarding.creatorPlatforms.instagramReelsDesc'), icon: <FiImage size={16} /> },
        { value: 'TikTok', label: t('onboarding.creatorPlatforms.tiktokShort'), description: t('onboarding.creatorPlatforms.tiktokShortDesc'), icon: <FiMonitor size={16} /> },
        { value: 'X / Twitter', label: t('onboarding.creatorPlatforms.twitterX'), description: t('onboarding.creatorPlatforms.twitterXDesc'), icon: <FiMessageSquare size={16} /> },
        { value: 'LinkedIn', label: t('onboarding.creatorPlatforms.linkedinPro'), description: t('onboarding.creatorPlatforms.linkedinProDesc'), icon: <FiUsers size={16} /> },
        { value: 'Twitch', label: t('onboarding.creatorPlatforms.twitch'), description: t('onboarding.creatorPlatforms.twitchDesc'), icon: <FiMonitor size={16} /> },
        { value: 'Podcast', label: t('onboarding.creatorPlatforms.podcast'), description: t('onboarding.creatorPlatforms.podcastDesc'), icon: <FiMic size={16} /> },
        { value: 'Webpage', label: t('onboarding.creatorPlatforms.websitePersonal'), description: t('onboarding.creatorPlatforms.websitePersonalDesc'), icon: <FiGlobe size={16} /> },
        { value: 'Newsletter / Email', label: t('onboarding.creatorPlatforms.newsletterEmail'), description: t('onboarding.creatorPlatforms.newsletterEmailDesc'), icon: <FiMessageSquare size={16} /> },
      ];

      return (
        <Form>
          <MultiSelectField
            label={t('onboarding.creatorServicesLabel') || 'What You Offer'}
            name="products_services"
            value={formData.products_services || []}
            options={creatorServicesOptions}
            onChange={onChange}
            max={3}
            description={t('onboarding.creatorServicesDesc') || 'What value do you provide? Select up to 3.'}
            icon={<FiPackage size={16} />}
            compact={true}
          />
          <MultiSelectField
            label={t('onboarding.creatorPlatformsLabel') || 'Platforms'}
            name="platforms"
            value={formData.platforms || []}
            options={creatorPlatformOptions}
            onChange={onChange}
            max={10}
            description={t('onboarding.creatorPlatformsDesc') || 'Where do you publish content?'}
            icon={<FiGlobe size={16} />}
            compact={true}
          />
        </Form>
      );
    }
  };

  // --- Render Step 4: Enhancement Hub ---
  const renderEnhancementHub = () => {
    const packs = [
      { id: 'personality', icon: <FiMic size={20} />, title: t('onboarding.packPersonality') || 'Personality & Role', desc: t('onboarding.packPersonalityDesc') || 'Define how your brand sounds and behaves.' },
      { id: 'audience', icon: <FiTarget size={20} />, title: t('onboarding.packAudience') || 'Audience & Market', desc: t('onboarding.packAudienceDesc') || 'Who are you talking to?' },
      { id: 'style', icon: <FiPenTool size={20} />, title: t('onboarding.packStyle') || 'Content & Style', desc: t('onboarding.packStyleDesc') || 'Formats, visuals, and energy.' },
      { id: 'values', icon: <FiHeart size={20} />, title: t('onboarding.packValues') || 'Values & Messaging', desc: t('onboarding.packValuesDesc') || 'What you stand for.' },
    ];

    const completedCount = packs.filter(p => isPackCompleted(p.id)).length;

    return (
      <div className="enhancement-hub">
        <p className="onboarding-text-muted mb-3">
          {t('onboarding.hubDesc') || 'Complete these optional packs to help BrandVox AI to understand your brand better.'}
        </p>

        {/* Progress */}
        <div className="enhancement-hub-progress mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small fw-semibold" style={{ color: 'var(--text-secondary, #6b7280)' }}>
              {completedCount} / 4
            </span>
            {completedCount === 4 && (
              <span className="small fw-semibold" style={{ color: '#10b981' }}>
                <FiCheckCircle size={14} className="me-1" />
                {t('onboarding.allPacksComplete') || 'All complete!'}
              </span>
            )}
          </div>
          <div className="enhancement-hub-progress-bar">
            <div
              className="enhancement-hub-progress-fill"
              style={{ width: `${(completedCount / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Pack Cards */}
        <div className="enhancement-hub-cards">
          {packs.map((pack) => {
            const completed = isPackCompleted(pack.id);
            return (
              <div
                key={pack.id}
                className={`enhancement-hub-card ${completed ? 'completed' : ''}`}
                onClick={() => setActivePackModal(pack.id)}
              >
                <div className={`enhancement-hub-card-icon ${completed ? 'completed' : ''}`}>
                  {completed ? <FiCheckCircle size={20} /> : pack.icon}
                </div>
                <div className="enhancement-hub-card-content">
                  <div className="enhancement-hub-card-title">{pack.title}</div>
                  <div className="enhancement-hub-card-desc">{pack.desc}</div>
                </div>
                <FiArrowRight size={16} className="enhancement-hub-card-arrow" />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!show) return null;

  const isLastStep = currentStep === TOTAL_STEPS;
  const isOptionalStep = currentStep > 1;

  return (
    <>
      {/* Overlay */}
      <div className="setup-modal-overlay" onClick={handleClose}>
        <div
          className={`setup-modal-card onboarding-page ${shaking ? 'setup-modal-shake' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="setup-modal-header">
            <div>
              <span className="setup-modal-step-label">
                {t('onboarding.setupModal.stepOf', { step: currentStep, total: TOTAL_STEPS }) || `STEP ${currentStep} OF ${TOTAL_STEPS}`}
              </span>
              <h5 style={{ margin: '0.25rem 0 0', fontWeight: 700 }}>
                {getStepTitle(currentStep)}
                {isOptionalStep && (
                  <span className="setup-modal-optional-badge ms-2">
                    {t('onboarding.setupModal.optional') || 'OPTIONAL'}
                  </span>
                )}
              </h5>
            </div>
            <button className="setup-modal-close" onClick={handleClose} title={t('common.cancel') || 'Close'}>
              <FiX size={18} />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="setup-modal-progress">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => {
              const stepNum = i + 1;
              let className = 'setup-modal-progress-segment';
              if (stepNum < currentStep) className += ' completed';
              else if (stepNum === currentStep) className += ' active';
              return <div key={i} className={className} />;
            })}
          </div>

          {/* Body */}
          <div className="setup-modal-body" ref={bodyRef}>
            {currentStep === 1 && renderBrandBasics()}
            {currentStep === 2 && renderIdentityCore()}
            {currentStep === 3 && renderPresenceOfferings()}
            {currentStep === 4 && renderEnhancementHub()}
          </div>

          {/* Footer */}
          <div className="setup-modal-footer">
            {currentStep > 1 ? (
              <CancelButton onClick={handleBack}>
                <FiArrowLeft className="me-2" /> {t('common.back') || 'Back'}
              </CancelButton>
            ) : (
              <div />
            )}

            {isLastStep ? (
              <PrimaryButton onClick={handleFinish} padding="0.75rem 1.5rem" fontSize="15px">
                {t('onboarding.setupModal.finish') || 'Finish'} <FiCheck className="ms-2" />
              </PrimaryButton>
            ) : (
              <PrimaryButton
                onClick={handleContinue}
                disabled={isCreating || (currentStep === 1 && !formData.company_name?.trim())}
                padding="0.75rem 1.5rem"
                fontSize="15px"
              >
                {isCreating ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    {t('common.processing') || 'Processing...'}
                  </>
                ) : (
                  <>
                    {t('onboarding.setupModal.continue') || 'Continue'} <FiArrowRight className="ms-2" />
                  </>
                )}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>

      {/* Cropping Modal */}
      <Modal show={showCropModal} onHide={handleCropCancel} centered size="lg" backdrop="static" style={{ zIndex: 10000 }}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FiCrop size={20} className="me-2" style={{ color: 'var(--primary)' }} />
            {t('branding.brandsettings.cropTitle') || 'Crop Logo'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          {cropError && (
            <Alert variant="danger" className="m-3 mb-0">
              {cropError}
            </Alert>
          )}
          {imgSrc ? (
            <>
              <div style={{ position: 'relative', height: '350px', backgroundColor: '#f4f4f5' }}>
                <Cropper
                  image={imgSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  cropShape="round"
                  showGrid={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
              <div className="d-flex align-items-center justify-content-center gap-3 py-3 border-top bg-white">
                <span className="text-muted small fw-semibold">
                  {t('branding.brandsettings.cropZoom') || 'Zoom'}
                </span>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: '200px', accentColor: 'var(--primary)' }}
                />
                <span className="text-muted small fw-semibold">{Math.round(zoom * 100)}%</span>
              </div>
            </>
          ) : (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <CancelButton onClick={handleCropCancel}>
            {t('branding.brandsettings.cropCancel') || 'Cancel'}
          </CancelButton>
          <PrimaryButton onClick={handleCropImage} disabled={!croppedAreaPixels}>
            {t('branding.brandsettings.cropSave') || 'Save'}
          </PrimaryButton>
        </Modal.Footer>
      </Modal>

      {/* Pack Modal (Enhancement Hub) */}
      {activePackModal && (
        <PackModal
          show={!!activePackModal}
          onHide={() => setActivePackModal(null)}
          packId={activePackModal}
          brandType={brandType}
          formData={formData}
          onChange={onChange}
          onSave={async () => {
            setIsSavingPack(true);
            try {
              if (brandCreated && onUpdateBrand) {
                if (debounceRef.current) clearTimeout(debounceRef.current);
                await onUpdateBrand();
              }
            } finally {
              setIsSavingPack(false);
              setActivePackModal(null);
            }
          }}
          isSaving={isSavingPack}
          aiSuggestions={aiSuggestions}
        />
      )}
    </>
  );
};

export default SetupModal;
